import { policiesContext, WidgetConstants } from "../../utils/common";
import { Box, CircularProgress, Grid, IconButton, Typography } from "@mui/material";
import gear from "../../../public/assets/icons/gear.svg";
import WorkItemTable from "../organisms/workItemsTable";
import React, { useEffect } from "react";
import QualityAssessmentSettings from "../molecules/QualityAssessmentSettings";
import NavigationColumn from "../molecules/NavigationColumn";
import { getAvailableStatuses } from "../molecules/ProjectSelectColumn";
import { WorkitemStatus } from "../molecules/ProjectSelectColumn/types";
import { GraderCountsRow } from "../molecules/GraderCountsRow";
import { useHistory, useLocation } from "react-router-dom";
import theme from "../../theme/Theme";
import { isMultiUserMode } from "../../utils/cqa/assignmentUtil";
import { Collection } from "../../types/sd";
interface IProps {
    client: any;
    widgetOptions: any;
    directoryType: string;
}

const QualityAssessment = (props: IProps) => {
    const { client, widgetOptions, directoryType } = props;
    const [qualityAssessmentSettingsOpen, setQualityAssessmentSettingsOpen] = React.useState(false);
    const { roles, userContext } = React.useContext(policiesContext);
    const availableStatuses = getAvailableStatuses(userContext);
    const history = useHistory();
    const location = useLocation();

    // Read initial project and status from URL params
    const urlParams = new URLSearchParams(location.search);
    const initialProjectId = urlParams.get('projectId');
    const initialStatus = urlParams.get('status') as WorkitemStatus;

    const [selectedProject, setSelectedProject] = React.useState<Collection>(null);
    const [workitemStatus, setWorkitemStatus] = React.useState<WorkitemStatus | null>(initialStatus || null);
    const [countIsLoading, setCountIsLoading] = React.useState<boolean>(true);
    const [loadingProjectList, setLoadingProjectList] = React.useState<boolean>(true);
    // const [projectVersion, setProjectVersion] = React.useState(0);

    // Managers in multi-user / compare projects group work items in the worklist and prev/next cursor;
    // the virtual ASSIGNED_TO_ME tab (manager-only) uses coder-like flat navigation instead.
    const [groupByCQAWorkItemId, setGroupByCQAWorkItemId] = React.useState(false);

    const handleProjectSelect = (project: any, status?: WorkitemStatus | null) => {
        // Default to first available status (Remaining Cases/To do) when project is selected
        // Only use null if explicitly needed e.g., showing all items
        const statusToApply = (status !== undefined && status !== null) ? status : (availableStatuses?.[0] || null);
        setSelectedProject(project);
        setWorkitemStatus(statusToApply);
        // setProjectVersion(prev => prev + 1);

        // Update URL with selected project and status
        const params = new URLSearchParams(location.search);
        if (project?.collection_id) {
            params.set('projectId', project.collection_id);
        }
        else params.delete('projectId');

        if (statusToApply) {
            params.set('status', statusToApply);
        }
        else params.delete('status');

        history.replace({ search: params.toString(), state: location.state });

        const multiUserMode = isMultiUserMode(project?.assignee_ids, project?.project_configuration);
        const groupForManager =
            roles?.includes("cqa_manager") &&
            multiUserMode &&
            statusToApply !== WorkitemStatus.ASSIGNED_TO_ME;
        setGroupByCQAWorkItemId(groupForManager);
    }

    const handleStatusChange = (status: WorkitemStatus | null) => {
        setWorkitemStatus(status);
        // setProjectVersion(prev => prev + 1);

        const params = new URLSearchParams(location.search);
        if (selectedProject?.collection_id) {
            params.set('projectId', selectedProject.collection_id);
        }
        if (status) {
            params.set('status', status);
        } else {
            params.delete('status');
        }
        history.replace({ search: params.toString(), state: location.state });
    };

    const handleProjectUpdated = (updatedProject: any) => {
        if (updatedProject && selectedProject?.collection_id === updatedProject.collection_id) {
            setSelectedProject(updatedProject);
            // setProjectVersion(prev => prev + 1);
        }
    };

    useEffect(() => {
        const multiUserMode = isMultiUserMode(selectedProject?.assignee_ids, selectedProject?.project_configuration);
        const groupForManager =
            roles?.includes("cqa_manager") &&
            multiUserMode &&
            workitemStatus !== WorkitemStatus.ASSIGNED_TO_ME;
        setGroupByCQAWorkItemId(groupForManager);
    }, [roles, selectedProject, workitemStatus])

    let filtersWithStatus = [];
    if (selectedProject?.filters) {
        if (workitemStatus) {
            filtersWithStatus = [
                ...selectedProject?.filters.filter((filter: any) => filter.key !== "qa_status" && filter.key !== "workitem.qa_status"),
            ];

            if (workitemStatus == WorkitemStatus.ASSIGNED_TO_ME) {
                if (roles?.includes("cqa_manager")) {
                    filtersWithStatus.push(...[
                        {
                            value: "MY_CASES",
                            key: "assignee",
                            operation: "eq"
                        },
                        { value: WorkitemStatus.IN_CQA, key: "workitem.qa_status", operation: "eq" }
                    ]);
                }
            } else {
                filtersWithStatus.push({ value: workitemStatus, key: "workitem.qa_status", operation: "eq" });
            }
        }
        else {
            filtersWithStatus = [
                // When the app first loads the filter key is qa_status and the logic above modifies it to workitem.qa_status
                ...selectedProject?.filters.filter((filter) => !(filter.key === "qa_status" || filter.key === "workitem.qa_status")),
                {
                    value: availableStatuses.filter((status) => status !== WorkitemStatus.ASSIGNED_TO_ME),
                    key: "workitem.qa_status",
                    operation: "in"
                }
            ];
        }
    }

    const multiUserMode = isMultiUserMode(selectedProject?.assignee_ids, selectedProject?.project_configuration);
    const graderCountsRow = roles?.includes("cqa_manager") && selectedProject && multiUserMode ? (<GraderCountsRow client={client} project={selectedProject} />) : null;

    if (!directoryType) return <></>;
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            height: '100vh',
            overflow: 'hidden'
        }}>
            <NavigationColumn
                mode={"qa"}
                client={client}
                onProjectSelected={handleProjectSelect}
                selectedProject={selectedProject}
                selectedStatus={workitemStatus}
                initialProjectId={initialProjectId}
                initialStatus={initialStatus}
                onCountIsLoadingChange={setCountIsLoading}
                onProjectUpdated={handleProjectUpdated}
                setLoadingProjectList={setLoadingProjectList}
            />
            <Box sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                width: '100%',
                padding: '2.5rem',
            }}>
                {loadingProjectList && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%'
                    }}>
                        <CircularProgress size={80} />
                    </Box>
                )}
                {
                    !loadingProjectList && (
                        <WorkItemTable
                            DirectoryType={directoryType}
                            isQualityAssessment={true}
                            widgetType={WidgetConstants.CQA}
                            widgetTitle={
                                <Typography variant="h2" component="span">
                                    {WidgetConstants.QUALITY_ASSESSMENT}
                                    {selectedProject?.name && (
                                        <>
                                            <span> /</span>
                                            <span style={{ color: theme.palette.artisticTextHigh.main }}>
                                                {" "}
                                                {selectedProject.name}
                                            </span>
                                        </>
                                    )}
                                </Typography>
                            }
                            cqaWorkitemStatus={workitemStatus}

                            widgetTitleActions={
                                <Grid container justifyContent="flex-end">
                                    {roles?.includes("cqa_manager") ? (
                                        <IconButton onClick={() => setQualityAssessmentSettingsOpen(true)}>
                                            <img src={gear} alt="Settings" />
                                        </IconButton>
                                    ) : null}
                                </Grid>
                            }

                            client={client}
                            handleRowClick={(workitem, data) => {
                                widgetOptions?.onWorkItemSelected(workitem, {
                                    ...data,
                                    isQualityAssessment: true,
                                    selectedProjectId: selectedProject?.collection_id,
                                    selectedStatus: workitemStatus,
                                });
                            }}

                            overridingViewFilters={filtersWithStatus as any}
                            groupByCQAWorkItemId={groupByCQAWorkItemId}
                            handleViewWorkitemData={(workitem) => widgetOptions?.viewWorkitemData(workitem)}
                            GraderCountsRow={graderCountsRow}
                            shouldLoadData={!!selectedProject}
                            projectIdentifier={selectedProject ? `${selectedProject.collection_id}-${workitemStatus || 'all'}` : undefined}
                            selectedProject={selectedProject}
                            selectedStatus={workitemStatus}
                            availableStatuses={availableStatuses}
                            onStatusChange={handleStatusChange}
                            countIsLoading={countIsLoading}
                        />
                    )
                }
                <QualityAssessmentSettings isOpen={qualityAssessmentSettingsOpen} onClose={() => setQualityAssessmentSettingsOpen(false)} client={client} />
            </Box>
        </Box>
    );
}

export default QualityAssessment;
